import { PopoverBody, PopoverArrow, Badge } from "@chakra-ui/react";
import { gql } from "__generated__/gql";
import CopyableCode from "components/Shared/CopyableCode";
import { deliveryMethodLabelsByType } from "components/Shared/DeliveryMethodButtons";
import getMaybeList from "components/Shared/getMaybeList";
import LabeledValueTable, {
  LabeledValueRow,
} from "components/Shared/LabeledValueTable";
import Link from "components/Shared/Link";
import SkuPreviewPopover from "components/Shared/SkuPreviewPopover";
import { useAdminQuery } from "utils/apollo/hooks/admin";
import { makeFormatCurrency } from "utils/formatting";
import { customerPage, customerWarehousePage, selectedSku } from "utils/links";

const query = gql(/* GraphQL */ `
  query CustomerPresencePopoverContent($skuBrandOrderingPlatformId: UUID!) {
    skuBrandOrderingPlatform(id: $skuBrandOrderingPlatformId) {
      id
      itemCode
      wholesaleUnitPrice
      sku {
        id
        fullNameShort
      }
      brandOrderingPlatform {
        id
        supplierCode
        deliveryMethod
        orderingPlatform {
          id
          name
        }
      }
      skuBrandDistributionCentersList(
        orderBy: DISTRIBUTION_CENTER_NAME_ASC
        filter: {
          brandDistributionCenter: {
            distributionCenter: { name: { isNull: false } }
          }
        }
      ) {
        id
        brandDistributionCenter {
          id
          distributionCenter {
            id
            name
          }
        }
      }
    }
  }
`);

type Props = {
  skuBrandOrderingPlatformId: UUID;
};

export default function CustomerPresencePopoverContent({
  skuBrandOrderingPlatformId,
}: Props) {
  const { data, loading } = useAdminQuery(query, {
    skip: !skuBrandOrderingPlatformId,
    variables: { skuBrandOrderingPlatformId },
    returnPartialData: true,
  });

  const sbop = data?.skuBrandOrderingPlatform;
  const bop = sbop?.brandOrderingPlatform;

  return (
    <>
      <PopoverArrow />
      <PopoverBody>
        <LabeledValueTable
          isLoaded={!loading}
          labelProps={{ width: "14em", textAlign: "right" }}
          valueProps={{ lineHeight: "normal" }}
          skeletonProps={{ alignItems: "flex-start" }}
          sx={{ tableLayout: "fixed" }}
          width="100%"
        >
          <LabeledValueRow
            label="Ordering Platform"
            value={
              bop?.orderingPlatform && (
                <Link href={customerPage(bop.orderingPlatform.id)}>
                  {bop.orderingPlatform.name}
                </Link>
              )
            }
          />

          <LabeledValueRow
            label="Supplier Code"
            value={
              bop?.supplierCode && <CopyableCode code={bop.supplierCode} />
            }
          />

          <LabeledValueRow
            label="Delivery Method"
            value={
              bop?.deliveryMethod && (
                <Badge variant="outline">
                  {deliveryMethodLabelsByType?.[bop.deliveryMethod]}
                </Badge>
              )
            }
          />

          <LabeledValueRow
            label="Product"
            value={
              sbop?.sku && (
                <SkuPreviewPopover skuId={sbop.sku.id} placement="right">
                  <Link href={selectedSku(sbop.sku.id)}>
                    {sbop.sku.fullNameShort}
                  </Link>
                </SkuPreviewPopover>
              )
            }
          />

          <LabeledValueRow
            label="Item Code"
            value={sbop?.itemCode && <CopyableCode code={sbop.itemCode} />}
          />

          <LabeledValueRow
            label="Wholesale Unit Price"
            value={makeFormatCurrency({
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })(sbop?.wholesaleUnitPrice)}
          />

          <LabeledValueRow
            label={`Distribution Center${
              sbop?.skuBrandDistributionCentersList?.length === 1 ? "" : "s"
            }`}
            valueProps={{
              sx: { li: { my: ".3em" } },
            }}
            value={
              sbop?.skuBrandDistributionCentersList?.length
                ? getMaybeList(
                    sbop?.skuBrandDistributionCentersList?.map((sbdc) => (
                      <Link
                        key={sbdc.id}
                        href={customerWarehousePage(
                          bop?.orderingPlatform?.id,
                          sbdc.brandDistributionCenter?.distributionCenter?.id,
                        )}
                      >
                        {sbdc.brandDistributionCenter?.distributionCenter?.name}
                      </Link>
                    )),
                  )
                : undefined
            }
          />
        </LabeledValueTable>
      </PopoverBody>
    </>
  );
}
